/**
 * Scope catalogue for rebate split overrides.
 *
 * The static `SYMBOL_CATALOGUE` / `ASSET_CLASSES` are a mock fallback for
 * Storybook / tests / vibe-coding. Runtime code paths call the `to*` adapters
 * with a fetched `AssetGroup[]`.
 */
import type { AssetGroup } from "../data";

export type CatalogueSymbol = {
  symbol: string;
  assetClass: string;
};

export const ASSET_CLASSES = [
  "Forex",
  "Commodities",
  "Indices",
  "Crypto",
  "Shares",
];

export const SYMBOL_CATALOGUE: CatalogueSymbol[] = [
  { symbol: "AUDUSD", assetClass: "Forex" },
  { symbol: "EURUSD", assetClass: "Forex" },
  { symbol: "GBPUSD", assetClass: "Forex" },
  { symbol: "USDJPY", assetClass: "Forex" },
  { symbol: "USDCAD", assetClass: "Forex" },
  { symbol: "XAUUSD", assetClass: "Commodities" },
  { symbol: "XAGUSD", assetClass: "Commodities" },
  { symbol: "XTIUSD", assetClass: "Commodities" },
  { symbol: "US500", assetClass: "Indices" },
  { symbol: "NAS100", assetClass: "Indices" },
  { symbol: "GER40", assetClass: "Indices" },
  { symbol: "BTCUSD", assetClass: "Crypto" },
  { symbol: "ETHUSD", assetClass: "Crypto" },
  { symbol: "AAPL", assetClass: "Shares" },
  { symbol: "TSLA", assetClass: "Shares" },
];

/**
 * The asset class a symbol belongs to, or `null` for an unknown symbol. Used to
 * detect a symbol rule that sits on top of an asset-class rule for the same
 * instrument — the "shadow" case the design annotates.
 *
 * Mock-backed — runtime callers use `buildAssetClassOfSymbol(assets)`.
 */
export const assetClassOfSymbol = (symbol: string): string | null =>
  SYMBOL_CATALOGUE.find((entry) => entry.symbol === symbol)?.assetClass ?? null;

/** Adapters from API shape (grouped) to component shape (flat); null falls back to the mock. */
export const toCatalogueSymbols = (
  assets: AssetGroup[] | null | undefined,
): CatalogueSymbol[] => {
  if (!assets) return SYMBOL_CATALOGUE;
  return assets.flatMap((group) =>
    (group.symbols ?? []).map((symbol) => ({
      symbol,
      assetClass: group.name,
    })),
  );
};

export const toAssetClasses = (
  assets: AssetGroup[] | null | undefined,
): string[] => {
  if (!assets) return ASSET_CLASSES;
  return assets.map((group) => group.name);
};

export const buildAssetClassOfSymbol =
  (assets: AssetGroup[] | null | undefined) =>
  (symbol: string): string | null => {
    if (!assets) return assetClassOfSymbol(symbol);
    for (const group of assets) {
      if ((group.symbols ?? []).includes(symbol)) return group.name;
    }
    return null;
  };
